/**
 * The bits of a drawn order that both orders share.
 *
 * `materialOrderPdf.ts` came first and had all of this inside it. A work order
 * now goes out as a file too — Lysa, 2026-09-12: "I can't tell if anything was
 * sent to the crews or distributor" — and it is the same page: a letterhead, a
 * block of labelled facts, a table of lines, a total. Copying three hundred
 * lines to change the words at the top is how the two documents would start
 * disagreeing about what a company's paperwork looks like, so the page furniture
 * lives here and each document keeps only its own layout.
 *
 * Nothing here knows what an order is. It draws labels, rules and money.
 */

import { formatCents, type Cents } from "@roof-hawk/core";

import { COMPANY_LOGO_URL } from "../logo";

/** Letter, in points, which is what jsPDF measures in. */
export const PAGE = { width: 612, height: 792 };
export const MARGIN = 54;
export const RIGHT = PAGE.width - 56;

export const INK = "#111111";
export const GREY = "#6b7280";
export const RULE = "#d4d4d8";

export type Doc = import("jspdf").jsPDF;

/**
 * The company's mark, as bytes a PDF can hold.
 *
 * A failure to load it is not a failure to order — the list goes out without
 * the badge rather than not at all. See `invoicePdf.ts`, which does the same.
 */
export async function logoDataUrl(
  url = COMPANY_LOGO_URL,
): Promise<string | null> {
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    const blob = await response.blob();
    return await new Promise<string | null>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

export function heading(doc: Doc, text: string, x: number, y: number): void {
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(GREY);
  doc.text(text, x, y);
  doc.setTextColor(INK);
}

export function rule(doc: Doc, y: number, from = MARGIN, to = RIGHT): void {
  doc.setDrawColor(RULE);
  doc.setLineWidth(0.7);
  doc.line(from, y, to, y);
}

/** A label-and-value pair in the header block. */
export function pair(
  doc: Doc,
  label: string,
  value: string,
  x: number,
  y: number,
): void {
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(GREY);
  doc.text(label, x, y);
  doc.setFontSize(10.5);
  doc.setTextColor(INK);
  doc.text(value || "—", x, y + 15);
}

/**
 * A label-and-value pair wide enough for a sentence.
 *
 * The address is the one thing on a crew's copy that gets read at a kerb, and
 * "1234 Something Long Drive, Birmingham, AL 35226" does not fit in a column.
 */
export function wide(
  doc: Doc,
  label: string,
  value: string,
  x: number,
  y: number,
  width = RIGHT - MARGIN,
): void {
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(GREY);
  doc.text(label, x, y);
  doc.setFontSize(11);
  doc.setTextColor(INK);
  doc.text(doc.splitTextToSize(value || "—", width), x, y + 15);
}

/** One money row, right-aligned on the two number columns. */
export function money(
  doc: Doc,
  label: string,
  value: Cents,
  at: number,
  columns: { label: number; value: number },
  big = false,
): void {
  doc.setFont("helvetica", "normal");
  doc.setFontSize(big ? 15 : 10.5);
  doc.setTextColor(big ? INK : GREY);
  doc.text(label, columns.label, at, { align: "right" });
  doc.setTextColor(INK);
  doc.text(formatCents(value), columns.value, at, { align: "right" });
}
